import { clientEnv } from '@/shared/config/env';
import { requestJson } from '@/shared/request/http-client';
import { trackMeta } from '@/shared/tracking/meta-pixel';

export type GameNavMode = 'CATEGORY_FIRST' | 'FLAT' | 'VENUE_ONLY';
export type GameNavNodeType = 'CATEGORY' | 'COLLECTION' | 'VENUE';

export interface GameSummary {
  badge: string | null;
  category: string;
  coverUrl: string | null;
  gameCode: string;
  hot: boolean;
  isNew: boolean;
  /** 当前供应厂商 */
  providerCode: string;
  supportsDemo: boolean;
  title: string;
  /** 场馆=平台码 */
  venueCode: string;
}

export interface GameNavNode {
  children: GameNavNode[];
  count: number | null;
  iconUrl: string | null;
  key: string;
  label: string;
  refCode: string;
  type: GameNavNodeType;
}

export interface GameNav {
  navMode: GameNavMode;
  nodes: GameNavNode[];
}

export interface GameHomeSection {
  items: GameSummary[];
  key: string;
}

export interface GameHome {
  navMode: GameNavMode;
  nodes: GameNavNode[];
  sections: GameHomeSection[];
}

export interface GamePage {
  hasMore: boolean;
  items: GameSummary[];
  pageNo: number;
  pageSize: number;
  total: number;
}

export interface GameLaunch {
  gameCode: string;
  launchUrl: string | null;
  message: string | null;
  status: string;
}

export interface GameFavoriteState {
  favorited: boolean;
  gameCode: string;
}

export interface GameListParams {
  category?: string;
  collection?: string;
  keyword?: string;
  pageNo?: number;
  pageSize?: number;
  venue?: string;
}

const siteParam = () => `siteId=${encodeURIComponent(clientEnv.siteId)}`;

export const listParamsFromNavNode = (node: GameNavNode): GameListParams => {
  if (node.type === 'VENUE') return { venue: node.refCode };
  if (node.type === 'COLLECTION') return { collection: node.refCode };
  return { category: node.refCode };
};

/** 导航配置（站点模式 + 可见节点），游客可访问。 */
export const getGameNav = () =>
  requestJson<GameNav>(`/v1/app/game/nav?${siteParam()}`, { method: 'GET' });

/** 大厅聚合（导航 + 分类/合集首屏），游客可访问。 */
export const getGameHome = (sectionSize = 60) => {
  const search = new URLSearchParams({
    siteId: clientEnv.siteId,
    sectionSize: String(sectionSize),
  });
  return requestJson<GameHome>(`/v1/app/game/home?${search.toString()}`, { method: 'GET' });
};

/** 游戏列表（分类/场馆/合集/关键词过滤分页），游客可访问。 */
export const listGames = (params: GameListParams = {}) => {
  const search = new URLSearchParams({ siteId: clientEnv.siteId });
  if (params.category) search.set('category', params.category);
  if (params.venue) search.set('venue', params.venue);
  if (params.collection) search.set('collection', params.collection);
  if (params.keyword) search.set('keyword', params.keyword);
  search.set('pageNo', String(params.pageNo ?? 1));
  search.set('pageSize', String(params.pageSize ?? 24));
  return requestJson<GamePage>(`/v1/app/game/list?${search.toString()}`, { method: 'GET' });
};

/** 有上限的兜底拉取。大厅 Estudios / 分类网格必须走 `listGames` 分页，不要用这个倒全量。 */
export const listAllGames = async (params: Omit<GameListParams, 'pageNo' | 'pageSize'> = {}) => {
  const items: GameSummary[] = [];
  let pageNo = 1;
  let hasMore = true;
  while (hasMore && pageNo <= 10) {
    const page = await listGames({ ...params, pageNo, pageSize: 60 });
    items.push(...page.items);
    hasMore = page.hasMore;
    pageNo += 1;
  }
  return items;
};

/** 启动游戏（登录必需）。失败时由调用方区分鉴权/未开放。 */
export const launchGame = async (gameCode: string, authToken: string) => {
  const launched = await requestJson<GameLaunch>(
    `/v1/app/game/launch/${encodeURIComponent(gameCode)}?${siteParam()}`,
    { authToken, method: 'POST' },
  );
  if (launched.launchUrl) {
    trackMeta('gameLaunch', {
      contentIds: [gameCode],
      eventId: `gl_${gameCode}_${Date.now()}`,
    });
  }
  return launched;
};

export const listFavoriteGames = (authToken: string, pageNo = 1, pageSize = 60) => {
  const search = new URLSearchParams({
    siteId: clientEnv.siteId,
    pageNo: String(pageNo),
    pageSize: String(pageSize),
  });
  return requestJson<GamePage>(`/v1/app/game/favorites?${search.toString()}`, {
    authToken,
    method: 'GET',
  });
};

export const getFavoriteState = (gameCode: string, authToken: string) =>
  requestJson<GameFavoriteState>(
    `/v1/app/game/favorites/${encodeURIComponent(gameCode)}?${siteParam()}`,
    { authToken, method: 'GET' },
  );

export const addFavoriteGame = (gameCode: string, authToken: string) =>
  requestJson<GameFavoriteState>(
    `/v1/app/game/favorites/${encodeURIComponent(gameCode)}?${siteParam()}`,
    { authToken, method: 'POST' },
  );

export const removeFavoriteGame = (gameCode: string, authToken: string) =>
  requestJson<GameFavoriteState>(
    `/v1/app/game/favorites/${encodeURIComponent(gameCode)}?${siteParam()}`,
    { authToken, method: 'DELETE' },
  );

export interface PlayerGameBetItem {
  betAmountMinor: number;
  betId: string;
  createdAt: string;
  currency: string;
  gameTitle: string;
  profitLossMinor: number;
  winAmountMinor: number;
}

export interface PlayerGameBetPage {
  hasMore: boolean;
  items: PlayerGameBetItem[];
  pageNo: number;
  pageSize: number;
  total: number;
}

export const listPlayerBets = (
  authToken: string,
  params: { pageNo?: number; pageSize?: number; range: string },
  signal?: AbortSignal,
) => {
  const search = new URLSearchParams({
    siteId: clientEnv.siteId,
    range: params.range,
    pageNo: String(params.pageNo ?? 1),
    pageSize: String(params.pageSize ?? 20),
  });
  return requestJson<PlayerGameBetPage>(`/v1/app/game/bets?${search.toString()}`, {
    authToken,
    method: 'GET',
    signal,
  });
};

export const listHistoryGames = (authToken: string, pageNo = 1, pageSize = 60) => {
  const search = new URLSearchParams({
    siteId: clientEnv.siteId,
    pageNo: String(pageNo),
    pageSize: String(pageSize),
  });
  return requestJson<GamePage>(`/v1/app/game/history?${search.toString()}`, {
    authToken,
    method: 'GET',
  });
};
